import { FC, useEffect, useState } from 'react';

import { useCms } from '@eneco/cms';
import { linkWithUrl } from '@eneco/cms-types';
import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { typedYupResolver } from '@eneco/hooks/form';
import {
  ButtonLink,
  CheckTile,
  CheckboxGroup,
  Heading,
  Image,
  NotificationBox,
  Stack,
  Stretch,
  TextLink,
} from '@eneco/sparky';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { ChoiceStepFooter, Header, Layout } from '../../components';
import { useStepContent, useStepKey } from '../../hooks';
import { FlowContext } from '../../types';
import { useFlowHooks } from '../../utils/FlowProvider';
import { getIllustration } from '../../utils/getIllustration';
import { readChoiceOptions } from '../../utils/readChoiceOptions';
import { readContentSection } from '../../utils/readContentSection';
import { readMoreInfoDialogs } from '../../utils/readMoreInfoDialogs';

interface FormFields {
  [key: string]: string[] | string | undefined;
}

export const GenericCheckboxChoiceStep = () => {
  // This step won't re-render properly when used on multiple consecutive steps
  // without a key that changes per step page. See `useStepKey` for why the
  // rendering's `uid` is not that key.
  return <GenericCheckboxChoice key={useStepKey()} />;
};

const GenericCheckboxChoice: FC = () => {
  const { useFlowActorRef, useFlowSelector } = useFlowHooks();
  const sendToFlowMachine = useFlowActorRef().send;
  const flowContext = useFlowSelector(state => state.context);
  const { adapter } = useCms();

  const textualData = useStepContent('GenericCheckboxChoiceStep');
  const { title, body } = readContentSection(textualData);
  const { options } = readChoiceOptions(textualData);
  // This step's only dialog is the `moreInfoDialog` section, with its image and button.
  const { sectionDialog: dialog } = readMoreInfoDialogs(textualData);
  const dialogImage = adapter.toImage(dialog.image);
  const dialogButtonLink = linkWithUrl(dialog.buttonLink);

  const genericFieldName = textualData?.name ?? '';
  const genericFieldArrayValue = flowContext[genericFieldName as keyof FlowContext] || [];

  // Only the step's own message makes an answer required here, as it always has.
  const requiredMessage = textualData?.requiredMessageText;

  let formSchema = yup.object({
    [genericFieldName]: yup.array(yup.string()),
  });

  if (requiredMessage) {
    formSchema = yup.object({
      [genericFieldName]: yup.array(yup.string()).required(requiredMessage).min(1, requiredMessage),
    });
  }

  const {
    handleSubmit,
    control,
    getValues,
    watch,
    formState: { errors, isSubmitSuccessful },
  } = useForm<FormFields>({
    mode: 'onSubmit',
    defaultValues: {
      [genericFieldName]: genericFieldArrayValue as string[] | undefined,
    },
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  const [isModalOpen, setIsModalOpen] = useState(false);

  const { nonBlockingValue, blockingValue } = textualData ?? {};
  const mutatedGenericField = watch(genericFieldName);

  const showNonBlockingNotification =
    nonBlockingValue &&
    mutatedGenericField?.includes(nonBlockingValue) &&
    textualData?.nonBlockingChoiceNotification?.content;

  const showBlockingNotification =
    blockingValue &&
    mutatedGenericField?.includes(blockingValue) &&
    textualData?.errorBlockingChoiceNotification?.content;

  useEffect(() => {
    if (isSubmitSuccessful) {
      const formData = getValues();
      const genericFieldValue = formData[genericFieldName];

      sendToFlowMachine({
        type: 'NEXT_PAGE',
        values: {
          [genericFieldName]: genericFieldValue,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful]);

  const onOptionCheckboxChange = (
    name: string,
    value = genericFieldArrayValue as string[],
    onChange: (...event: unknown[]) => void,
  ) => (value.includes(name) ? onChange(value.filter(val => val !== name)) : onChange([...value, name]));

  return (
    <Layout.Content variant="A" handleSubmit={handleSubmit}>
      <Header>
        {title && <Header.Title>{title}</Header.Title>}

        {(dialog.triggerText || body) && (
          <Header.Text>
            <Stack gap="3" direction="column">
              {body && <RichText html={body} />}
              {dialog.triggerText && (
                <TrackedDialog
                  isOpen={isModalOpen}
                  setOpen={setIsModalOpen}
                  trigger={<TextLink emphasis="low">{dialog.triggerText}</TextLink>}>
                  <Stack gap="6" direction="column">
                    {dialogImage && (
                      <Image
                        src={dialogImage.src}
                        alt={dialogImage.alt || ''}
                        aspectRatio="16/9"
                        height="auto"
                        objectFit="cover"
                      />
                    )}

                    {dialog.title && (
                      <Heading size="S" as="h2">
                        {dialog.title}
                      </Heading>
                    )}

                    <RichText html={dialog.text} />

                    {dialogButtonLink && (
                      <Stack.Item>
                        <Stretch width={{ initial: true, lg: 'auto' }}>
                          <ButtonLink action="primary" target={dialogButtonLink.target} href={dialogButtonLink.href}>
                            {dialogButtonLink.text}
                          </ButtonLink>
                        </Stretch>
                      </Stack.Item>
                    )}
                  </Stack>
                </TrackedDialog>
              )}
            </Stack>
          </Header.Text>
        )}
      </Header>

      {textualData?.notification?.content && (
        <NotificationBox
          isAlert={false}
          variant={textualData.notification.variant}
          title={textualData.notification.title}
          text={<RichText html={textualData.notification.content} />}
        />
      )}

      {options.length > 0 && (
        <Controller
          control={control}
          name={genericFieldName}
          render={({ field: { value, onChange } }) => (
            <CheckboxGroup label="" error={errors?.[genericFieldName]?.message}>
              {options.map(({ name, label, illustration }) => {
                const Illustration = getIllustration(illustration);
                return (
                  <CheckTile
                    key={name}
                    name={name ?? ''}
                    icon={Illustration && <Illustration size="small" color="currentColor" />}
                    isChecked={value?.includes(name ?? '')}
                    onChange={() => onOptionCheckboxChange(name ?? '', value as string[], onChange)}>
                    {label ?? ''}
                  </CheckTile>
                );
              })}
            </CheckboxGroup>
          )}
        />
      )}

      <ChoiceStepFooter
        nonBlockingNotification={textualData?.nonBlockingChoiceNotification}
        blockingNotification={textualData?.errorBlockingChoiceNotification}
        isNonBlockingShown={Boolean(showNonBlockingNotification)}
        isBlocked={Boolean(showBlockingNotification)}
        nextStepText={textualData?.nextStepText}
        isLoading={isSubmitSuccessful}
      />
    </Layout.Content>
  );
};
